import { Link } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-red-600 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900">Privacy Policy</h1>
          </div>

          <div className="prose prose-slate max-w-none">
            <p className="text-slate-600 mb-8">
              CanadaTestPrep ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you access or use our website and educational platform (the "Service").
            </p>
            <p className="text-slate-600 mb-8">
              This policy is intended to comply with applicable Canadian privacy laws, including the Personal Information Protection and Electronic Documents Act (PIPEDA).
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">1. Information We Collect</h2>
            
            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">1.1 Information You Provide</h3>
            <p className="text-slate-600 mb-4">We may collect personal information when you:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Create an account</li>
              <li>Purchase access to the Service</li>
              <li>Submit a message through our contact form</li>
              <li>Request a refund under the Pass Guarantee</li>
              <li>Submit feedback or testimonials</li>
            </ul>
            <p className="text-slate-600 mb-4">This information may include:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Name</li>
              <li>Email address</li>
              <li>Billing information</li>
              <li>Account login credentials</li>
              <li>Citizenship test date (if voluntarily provided)</li>
              <li>Official test result documentation (if submitted for refund verification)</li>
              <li>Message content submitted through the contact form</li>
            </ul>
            <p className="text-slate-600 mb-4">
              Payment information is processed securely by third-party payment providers. We do not store full credit card details on our servers.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">1.2 Automatically Collected Information</h3>
            <p className="text-slate-600 mb-4">When you use the Service, we may automatically collect:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>IP address</li>
              <li>Device and browser type</li>
              <li>Operating system</li>
              <li>Pages visited</li>
              <li>Time spent on platform</li>
              <li>Study progress data</li>
              <li>Mock exam scores</li>
              <li>Simulation results</li>
              <li>Flashcard completion tracking</li>
            </ul>
            <p className="text-slate-600 mb-4">
              This information is used to operate the platform, improve performance, and verify eligibility for the Pass Guarantee.
            </p>

            <h3 className="text-lg font-semibold text-slate-800 mt-6 mb-3">1.3 Cookies and Analytics</h3>
            <p className="text-slate-600 mb-4">We may use cookies and analytics tools to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Maintain login sessions</li>
              <li>Track study progress</li>
              <li>Improve user experience</li>
              <li>Measure platform usage</li>
            </ul>
            <p className="text-slate-600 mb-4">
              You may disable cookies in your browser settings, though certain features may not function properly.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">2. How We Use Information</h2>
            <p className="text-slate-600 mb-4">We use collected information to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Provide access to the Service</li>
              <li>Process payments</li>
              <li>Track learning progress</li>
              <li>Administer the Pass Guarantee</li>
              <li>Respond to inquiries</li>
              <li>Improve platform functionality</li>
              <li>Send service-related communications</li>
            </ul>
            <p className="text-slate-600 mb-4 font-semibold">We do not sell personal information.</p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">3. Disclosure of Information</h2>
            <p className="text-slate-600 mb-4">We may share limited information with:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Payment processors</li>
              <li>Hosting providers</li>
              <li>Analytics providers</li>
              <li>Email or communication service providers</li>
              <li>Legal authorities if required by law</li>
            </ul>
            <p className="text-slate-600 mb-4 font-semibold">We do not sell or rent personal data.</p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">4. Data Storage and Security</h2>
            <p className="text-slate-600 mb-4">
              We implement reasonable technical and organizational safeguards to protect personal information. However, no method of transmission over the Internet can guarantee absolute security.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">5. Cross-Border Transfers</h2>
            <p className="text-slate-600 mb-4">
              Some service providers may store or process data outside Canada. By using the Service, you consent to such transfers.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">6. Data Retention</h2>
            <p className="text-slate-600 mb-4">We retain personal information only as long as necessary to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Provide the Service</li>
              <li>Fulfill refund obligations</li>
              <li>Comply with legal requirements</li>
              <li>Resolve disputes</li>
            </ul>
            <p className="text-slate-600 mb-4">You may request deletion of your account at any time.</p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">7. Your Rights</h2>
            <p className="text-slate-600 mb-4">You may request:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-1">
              <li>Access to your personal information</li>
              <li>Correction of inaccurate data</li>
              <li>Withdrawal of consent (subject to legal obligations)</li>
            </ul>
            <p className="text-slate-600 mb-4">
              To make a request, please contact us through our{" "}
              <Link to="/contact" className="text-red-600 hover:underline">contact form</Link>.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">8. Children</h2>
            <p className="text-slate-600 mb-4">
              The Service is intended for individuals 18 years or older. We do not knowingly collect personal information from children under 13.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">9. Updates to This Policy</h2>
            <p className="text-slate-600 mb-4">
              We may update this Privacy Policy from time to time. Continued use of the Service constitutes acceptance of any updates.
            </p>

            <h2 className="text-xl font-bold text-slate-900 mt-8 mb-4">10. Contact</h2>
            <p className="text-slate-600 mb-4">
              For privacy-related inquiries, please use our{" "}
              <Link to="/contact" className="text-red-600 hover:underline">contact form</Link>.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm">
          <p>© 2026 CanadaTestPrep.ca. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
